import { ReactNode } from "react";
import { Icon } from "@iconify/react";
import { card, colors, type, viz } from "../styles";
import { Freshness, FreshnessChip } from "./Pills";

export type StatTone = "neutral" | "good" | "warning" | "critical";

const TONE_ICON: Record<StatTone, string | undefined> = {
  neutral: undefined,
  good: "ion:checkmark-circle",
  warning: "ion:alert-circle",
  critical: "ion:close-circle",
};

const TONE_COLOR: Record<StatTone, string> = {
  neutral: colors.ink,
  good: viz.good,
  warning: viz.warning,
  critical: viz.critical,
};

/**
 * Label / value / optional secondary line.
 *
 * A non-neutral tone ships an icon alongside the color — a status color never
 * carries the meaning on its own (two of the four status steps are sub-3:1 on
 * white, and color alone excludes CVD readers regardless).
 */
const StatTile = ({
  label,
  value,
  secondary,
  tone = "neutral",
  onClick,
  title,
  freshness,
  children,
}: {
  label: string;
  value: ReactNode;
  secondary?: ReactNode;
  tone?: StatTone;
  onClick?: () => void;
  title?: string;
  /** Required, not optional — every real figure declares whether it's live or
   * from the nightly rollup, so two numbers never look the same on different
   * clocks by omission. */
  freshness: Freshness;
  /** Slot beneath the value — a sparkline, say. */
  children?: ReactNode;
}) => {
  const icon = TONE_ICON[tone];
  const interactive = Boolean(onClick);

  return (
    <div
      onClick={onClick}
      title={title}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? event => {
              if (event.key === "Enter" || event.key === " ") onClick?.();
            }
          : undefined
      }
      style={{
        ...card,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        cursor: interactive ? "pointer" : "default",
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ ...type.bodySmallMedium, color: colors.inkMuted }}>{label}</div>
        <FreshnessChip freshness={freshness} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {icon && <Icon icon={icon} height={18} style={{ color: TONE_COLOR[tone] }} />}
        <span
          style={{
            ...type.pageTitle,
            fontSize: 26,
            color: tone === "neutral" ? colors.ink : TONE_COLOR[tone],
          }}
        >
          {value}
        </span>
      </div>
      {secondary && (
        <div style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>{secondary}</div>
      )}
      {children}
    </div>
  );
};

export default StatTile;
